import { useTheme } from '@mui/material/styles'
import { PointSymbolProps, PointTooltip, ResponsiveLine } from '@nivo/line'
import { themeForRevenueLineGraph } from '../../FlyoutOverview/RevenueOverviewChart/RevenueOverviewChart.styles'
import {
  ChartAxisGroupingSelection,
  DEFAULT_CHART_AXIS_GROUPING,
  getSortedUniqueXDomain,
  mapSeriesToIndexedX,
  resolveBottomAxisLayout,
  resolveRankYAxisLayout,
} from './chartAxisUtils'

type LinePoints = {
  x: string | Date | number
  y: number
  label?: string
}

type ResponsiveLineChartData = {
  id: string
  color?: string
  data: LinePoints[]
}

type ResponsiveLineChartProps = {
  data: ResponsiveLineChartData[]
  pointSymbol?: (props: Readonly<PointSymbolProps>) => React.ReactElement
  reverse?: boolean
  gridYValues?: number
  leftTickValues?: number
  colors?: string[]
  leftAxisLegend?: string
  tooltip?: PointTooltip
  customColor?: string
  pointerSize?: number
  legendData?: string
  bottomTickValues?: boolean
  axisGroupingSelection?: ChartAxisGroupingSelection
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  renderTick?: (props: any) => JSX.Element
}

export const ResponsiveLineChart = ({
  data,
  pointSymbol,
  reverse = false,
  gridYValues,
  leftTickValues,
  colors,
  leftAxisLegend,
  tooltip,
  renderTick,
  customColor,
  pointerSize = 5,
  legendData,
  bottomTickValues,
  axisGroupingSelection = DEFAULT_CHART_AXIS_GROUPING,
}: ResponsiveLineChartProps) => {
  const theme = useTheme()
  const lineGraphTheme = themeForRevenueLineGraph(
    theme.palette.labraText.description,
    customColor
  )
  const chartSeries = data as Array<{
    data: Array<{ x: string | Date; [key: string]: unknown }>
  }>
  const xDomain = getSortedUniqueXDomain(chartSeries)
  const useEvenXAxis = bottomTickValues === true && xDomain.length > 0
  const bottomAxisLayout = resolveBottomAxisLayout(
    xDomain,
    bottomTickValues,
    axisGroupingSelection
  )
  const xScaleMin = bottomAxisLayout?.xScaleMin ?? 0
  const xScaleMax =
    bottomAxisLayout?.xScaleMax ?? Math.max(xDomain.length - 1, 0)
  const gridXValues =
    bottomAxisLayout && bottomAxisLayout.tickIndices.length > 1
      ? bottomAxisLayout.tickIndices
      : Array.from(
          { length: xScaleMax - xScaleMin + 1 },
          (_, index) => xScaleMin + index
        )
  const chartData = useEvenXAxis
    ? (mapSeriesToIndexedX(
        chartSeries,
        xDomain,
        bottomAxisLayout?.displayIndexByRawIndex
      ) as ResponsiveLineChartData[])
    : data

  const rankYAxisLayout = reverse
    ? resolveRankYAxisLayout(
        chartData as Array<{ data: Array<{ y?: number | null }> }>,
        leftTickValues ?? gridYValues ?? 10
      )
    : null
  const resolvedGridYValues = reverse
    ? rankYAxisLayout?.tickValues
    : gridYValues || leftTickValues
  const resolvedLeftTickValues = reverse
    ? rankYAxisLayout?.tickValues
    : leftTickValues

  const bottomMargin = legendData ? 50 : 36
  const bottomLegendOffset = legendData ? 42 : 0

  return (
    <ResponsiveLine
      data={chartData as ResponsiveLineChartData[]}
      theme={lineGraphTheme}
      curve="catmullRom"
      margin={{
        top: reverse ? 16 : 10,
        right: 50,
        bottom: bottomMargin,
        left: 60,
      }}
      xScale={
        useEvenXAxis
          ? {
              type: 'linear',
              min: xScaleMin,
              max: xScaleMax,
            }
          : { type: 'point' }
      }
      pointSize={pointerSize}
      yScale={{
        type: 'linear',
        stacked: false,
        reverse,
        ...(reverse && rankYAxisLayout
          ? { min: 0.5, max: rankYAxisLayout.max }
          : reverse
            ? { min: 0.5 }
            : {}),
      }}
      colors={colors}
      enableGridX={useEvenXAxis}
      gridXValues={useEvenXAxis ? gridXValues : undefined}
      gridYValues={resolvedGridYValues}
      axisBottom={{
        tickSize: 0,
        tickPadding: 10,
        legendOffset: bottomLegendOffset,
        legendPosition: 'middle',
        legend: legendData,
        ...(bottomAxisLayout?.tickIndices.length
          ? { tickValues: bottomAxisLayout.tickIndices }
          : {}),
        ...(renderTick ? { renderTick } : {}),
      }}
      useMesh
      enableCrosshair={false}
      axisLeft={{
        tickSize: 0,
        tickPadding: 10,
        legendOffset: -40,
        legendPosition: 'middle',
        legend: leftAxisLegend || '',
        tickValues: resolvedLeftTickValues,
        format: e => (Number.isInteger(e) ? e : ''),
      }}
      pointSymbol={pointSymbol}
      tooltip={tooltip}
    />
  )
}
